import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Cpu, Code2, GitBranch, ChevronRight } from "lucide-react";
import DiagnosticTool from "@/components/DiagnosticTool";

const pillars = [
  {
    id: "01",
    label: "AI SOLUTIONS",
    icon: Cpu,
    title: "AI Solutions & Products",
    body:
      "We build and integrate custom large language models, agentic workflows, and predictive data pipelines. We don't just paste API keys—we engineer intelligent, self-optimizing software layers tailored to your specific enterprise data.",
    points: [
      "Custom LLM & Agentic Workflows",
      "Automated Data Pipeline Synthesis",
      "Cognitive Automation & Proprietary AI Products",
    ],
  },
  {
    id: "02",
    label: "DIGITAL ENGINEERING",
    icon: Code2,
    title: "Digital Engineering & Design",
    body:
      "High-performance full-stack web architectures, premium application development, and hyper-intuitive UI/UX design. Built for scalability, military-grade security, and lightning-fast load velocities.",
    points: [
      "Multi-Tenant Cloud Architectures",
      "Premium Web & Mobile Apps",
      "Hyper-Intuitive UI/UX Systems",
    ],
  },
  {
    id: "03",
    label: "AGILITY",
    icon: GitBranch,
    title: "Project & Product Management",
    body:
      "Elite Agile delivery. We translate complex product visions into precise, trackable sprints. No missed deadlines, no communication gaps, and zero scope creep.",
    points: [
      "Agile Sprint Architecture",
      "Milestone Scoping & Mitigation",
      "Flawless Stakeholder Transparency",
    ],
  },
];

export default function Home() {
  const [active, setActive] = useState(0);
  const current = pillars[active];

  return (
    <div className="bg-[#0B0C10]">
      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden border-b border-[#1F2833]" data-testid="hero-section">
        {/* Right-anchored hero video (muted autoplay) */}
        <div
          className="absolute inset-y-0 right-0 w-full lg:w-[62%] pointer-events-none"
          aria-hidden="true"
        >
          <video
            data-testid="hero-video"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            className="absolute inset-0 w-full h-full object-cover opacity-70"
          >
            <source
              src="https://customer-assets-7cd3h4nn.emergentagent.net/job_cyber-forge-21/artifacts/8k26w88b_Initial_Scene_-_2026-07-28_202607272301.mp4"
              type="video/mp4"
            />
          </video>
          {/* Left-edge feather so the video melts into the dark canvas behind the headline */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B0C10] via-[#0B0C10]/70 to-transparent lg:from-[#0B0C10] lg:via-[#0B0C10]/50 lg:to-transparent" />
          {/* Bottom fade to blend with the section transition */}
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#0B0C10] to-transparent" />
        </div>

        <div className="absolute inset-0 te-grid-bg opacity-40" aria-hidden />
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 2.4, ease: "easeOut" }}
          className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-[#B91C2C]/10 blur-[140px]"
          aria-hidden
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 2.8, ease: "easeOut", delay: 0.4 }}
          className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-[#FF3355]/5 blur-[120px]"
          aria-hidden
        />

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-24 lg:pt-36 pb-24 lg:pb-40">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-8 flex items-center gap-3"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#FF3355] te-pulse-dot"></span>
            // System.init() — TraceElements v1.0
          </motion.div>

          <h1
            data-testid="hero-headline"
            className="font-display text-[#F5F5F7] text-4xl sm:text-6xl lg:text-8xl font-bold leading-[0.95] tracking-tighter max-w-5xl break-words"
          >
            We architect <span className="text-[#FF3355]">INTELLIGENT</span> INFRASTRUCTURE<span className="text-[#B91C2C]">.</span>
          </h1>

          <motion.p
            data-testid="hero-subheadline"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 text-base sm:text-lg text-[#C5C6C7] max-w-2xl leading-relaxed"
          >
            From deep-stack code to elite project execution. TraceElements builds the AI
            products, high-scale applications, and digital ecosystems that drive modern
            enterprise.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75, ease: [0.22, 1, 0.36, 1] }}
            className="mt-12 flex flex-wrap gap-4"
          >
            <Link
              to="/terminal"
              data-testid="hero-cta-initialize"
              className="group inline-flex items-center gap-2 bg-[#B91C2C] text-[#0B0C10] px-7 py-4 font-mono text-xs uppercase tracking-[0.22em] font-bold hover:bg-[#FF3355] transition-all hover:shadow-[0_0_30px_rgba(102,252,241,0.4)]"
            >
              [ Initialize Project ]
              <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
            <Link
              to="/solutions"
              data-testid="hero-cta-explore"
              className="inline-flex items-center gap-2 border border-[#B91C2C]/60 text-[#F5F5F7] px-7 py-4 font-mono text-xs uppercase tracking-[0.22em] hover:bg-[#B91C2C]/10 hover:border-[#FF3355] transition-all"
            >
              Explore Products
              <ChevronRight size={16} />
            </Link>
            <Link
              to="/work"
              data-testid="hero-cta-work"
              className="inline-flex items-center gap-2 border border-emerald-400/60 text-emerald-400 px-7 py-4 font-mono text-xs uppercase tracking-[0.22em] hover:bg-emerald-400/10 hover:border-emerald-400 transition-all"
            >
              See Our Work
              <ChevronRight size={16} />
            </Link>
          </motion.div>

          {/* Metrics strip */}
          <div className="mt-20 grid grid-cols-2 sm:grid-cols-4 gap-px bg-[#1F2833] border border-[#1F2833] max-w-4xl">
            {[
              ["02", "Co-Founders, zero middlemen"],
              ["100%", "Build velocity rate"],
              ["0", "Communication layers"],
              ["∞", "Engineering depth"],
            ].map(([num, label], i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.9 + i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className="bg-[#0B0C10] p-6"
              >
                <div className="font-display text-3xl text-[#FF3355] font-bold">{num}</div>
                <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.18em] text-[#C5C6C7]">{label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ TRI-PILLAR TERMINAL DASHBOARD ============ */}
      <section className="relative py-24 lg:py-32 border-b border-[#1F2833]" data-testid="pillars-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-6"
          >
            // OPERATIONAL CAPABILITIES
          </motion.div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#F5F5F7] max-w-3xl">
            Three pillars. <span className="text-[#FF3355]">One execution layer.</span>
          </h2>

          <div className="mt-14 te-terminal-window relative">
            {/* Terminal header */}
            <div className="border-b border-[#1F2833] bg-[#0B0C10] px-5 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full bg-[#1F2833] border border-[#B91C2C]/40"></span>
                <span className="h-2.5 w-2.5 rounded-full bg-[#1F2833] border border-[#B91C2C]/40"></span>
                <span className="h-2.5 w-2.5 rounded-full bg-[#FF3355]/80"></span>
              </div>
              <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#C5C6C7]/70">
                traceelements@core ~ /capabilities
              </div>
              <div className="font-mono text-[11px] text-[#FF3355]">ACTIVE</div>
            </div>

            {/* Tabs */}
            <div className="grid grid-cols-1 sm:grid-cols-3 border-b border-[#1F2833]">
              {pillars.map((p, idx) => {
                const Icon = p.icon;
                const isActive = idx === active;
                return (
                  <button
                    key={p.id}
                    onClick={() => setActive(idx)}
                    data-testid={`pillar-tab-${p.id}`}
                    className={`group text-left px-6 py-6 border-r border-[#1F2833] last:border-r-0 transition-all ${
                      isActive ? "bg-[#1F2833]/40" : "hover:bg-[#1F2833]/20"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`font-mono text-xs tracking-[0.22em] ${isActive ? "text-[#FF3355]" : "text-[#C5C6C7]"}`}>
                        [ {p.id} / {p.label} ]
                      </span>
                      <Icon size={16} className={isActive ? "text-[#FF3355]" : "text-[#C5C6C7]/60"} />
                    </div>
                    {isActive && (
                      <div className="mt-3 h-px w-12 bg-[#FF3355]" />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Active view */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
              <div className="lg:col-span-7 p-8 lg:p-12 border-b lg:border-b-0 lg:border-r border-[#1F2833]">
                <div className="font-mono text-xs text-[#B91C2C] mb-4">
                  &gt; Active View: {current.id} / {current.label}
                </div>
                <h3 className="font-display text-2xl lg:text-3xl font-bold tracking-tight">
                  {current.title}
                </h3>
                <p className="mt-5 text-[#C5C6C7] leading-relaxed max-w-xl">{current.body}</p>

                <div className="mt-8 space-y-2">
                  {current.points.map((pt, i) => (
                    <div key={i} className="te-lime-accent flex items-start gap-3 font-mono text-sm text-[#F5F5F7]/90">
                      <span className="text-[#FF3355] mt-0.5">&gt;</span>
                      <span>{pt}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="lg:col-span-5 p-8 lg:p-12 bg-[#0d1117] relative te-scanline">
                <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#B91C2C] mb-4">
                  // execution.log
                </div>
                <div className="space-y-3 font-mono text-xs text-[#C5C6C7]">
                  <div>[<span className="text-[#FF3355]">OK</span>] env.boot → systems online</div>
                  <div>[<span className="text-[#FF3355]">OK</span>] capability/{current.id} engaged</div>
                  <div>[<span className="text-[#FF3355]">OK</span>] dependencies resolved</div>
                  <div>[<span className="text-emerald-400">OK</span>] scope locked · {current.points.length} modules</div>
                  <div className="text-[#FF3355]">
                    &gt; awaiting client.signal<span className="te-caret">_</span>
                  </div>
                </div>

                <div className="mt-8 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-400 border border-emerald-400/40 px-3 py-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 te-pulse-dot"></span>
                  live system
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FOUNDER SYNERGY ============ */}
      <section className="relative py-24 lg:py-32 border-b border-[#1F2833]" data-testid="founders-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
            <div>
              <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-4">// THE ENGINE</div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight max-w-3xl">
                Two builders. <span className="text-[#FF3355]">One signal path.</span>
              </h2>
            </div>
            <Link to="/the-engine" className="font-mono text-xs uppercase tracking-[0.22em] text-[#FF3355] hover:text-[#F5F5F7] inline-flex items-center gap-2" data-testid="founders-cta-engine">
              Inspect the engine <ArrowUpRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-[#1F2833]">
            {/* Lao Dara — 6 cols */}
            <div className="lg:col-span-6 bg-[#0B0C10] p-8 lg:p-12 te-glow-hover border border-[#1F2833]" data-testid="founder-lao-card">
              <div className="flex items-start justify-between gap-6">
                <div>
                  <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#B91C2C]">// 01 ENGINEERING</div>
                  <h3 className="mt-4 font-display text-2xl lg:text-3xl font-bold">
                    Lao Dara
                  </h3>
                  <p className="font-mono text-sm text-[#C5C6C7] mt-1">Co-Founder &amp; Lead Stack Engineer</p>
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#FF3355] border border-[#FF3355]/40 px-3 py-1.5 shrink-0">
                  ENG · 01
                </div>
              </div>

              <p className="mt-8 text-[#C5C6C7] leading-relaxed max-w-2xl">
                &ldquo;The technical backbone. Specializing in complex system architectures, full-stack
                deployment, AI synthesis, and custom software mechanics. If it can be conceptualized,
                it will be built to scale.&rdquo;
              </p>

              <div className="mt-8 grid grid-cols-2 gap-px bg-[#1F2833] border border-[#1F2833]">
                {["Systems Architecture", "AI Synthesis", "Stack Deployment", "Scale Engineering"].map((s) => (
                  <div key={s} className="te-lime-accent bg-[#0B0C10] p-4 font-mono text-xs text-[#C5C6C7] flex items-center gap-2">
                    <span className="text-[#FF3355]">+</span>{s}
                  </div>
                ))}
              </div>
            </div>

            {/* Sherrie Dara — 6 cols */}
            <div className="lg:col-span-6 bg-[#0B0C10] p-8 lg:p-12 te-glow-hover border border-[#1F2833]" data-testid="founder-sherrie-card">
              <div className="flex items-start justify-between gap-6">
                <div>
                  <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#B91C2C]">// 02 OPERATIONS</div>
                  <h3 className="mt-4 font-display text-2xl lg:text-3xl font-bold">
                    Sherrie Dara
                  </h3>
                  <p className="font-mono text-sm text-[#C5C6C7] mt-1">Co-Founder &amp; Director of Operations</p>
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#FF3355] border border-[#FF3355]/40 px-3 py-1.5 shrink-0">
                  OPS · 02
                </div>
              </div>

              <p className="mt-8 text-[#C5C6C7] leading-relaxed">
                &ldquo;The execution engine. Mastering elite project lifecycle management, Agile scoping,
                day-to-day velocity control, and client-side communication. Translating engineering
                power into predictable market delivery.&rdquo;
              </p>

              <div className="mt-8 grid grid-cols-2 gap-px bg-[#1F2833] border border-[#1F2833]">
                {["Project Lifecycle", "Agile Scoping", "Velocity Control", "Client Comms"].map((s) => (
                  <div key={s} className="te-lime-accent bg-[#0B0C10] p-4 font-mono text-xs text-[#C5C6C7] flex items-center gap-2">
                    <span className="text-[#FF3355]">+</span>{s}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-10 border-l-2 border-[#FF3355] bg-[#1F2833]/40 p-6 font-mono text-sm text-[#C5C6C7] max-w-3xl">
            <span className="text-[#FF3355]">&gt;</span> We don&rsquo;t middleman your vision. The minds architecting
            your strategy are the exact hands building your systems.
          </div>
        </div>
      </section>

      {/* ============ DIAGNOSTIC TOOL ============ */}
      <section className="py-24 lg:py-32 border-b border-[#1F2833]" data-testid="diagnostic-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-4">
            // DIAGNOSTIC.SCAN
          </div>
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight max-w-3xl">
              Calibrate your <span className="text-[#FF3355]">build vector.</span>
            </h2>
            <p className="text-[#C5C6C7] max-w-md text-sm">
              Adjust the variables. Our diagnostic estimates structural requirements and surfaces the
              TraceElements track that aligns with your trajectory.
            </p>
          </div>
          <DiagnosticTool />
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="relative py-24 lg:py-36 overflow-hidden">
        <div className="absolute inset-0 te-grid-bg-dense opacity-40" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-6"
          >
            // READY.TO.BUILD
          </motion.div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tighter max-w-5xl mx-auto">
            Stop describing the future. <br />
            <span className="text-[#FF3355]">Start compiling it.</span>
          </h2>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <Link
              to="/terminal"
              data-testid="bottom-cta-initialize"
              className="mt-12 inline-flex items-center gap-3 bg-[#B91C2C] text-[#0B0C10] px-8 py-5 font-mono text-sm uppercase tracking-[0.22em] font-bold hover:bg-[#FF3355] transition-all hover:shadow-[0_0_40px_rgba(102,252,241,0.4)]"
            >
              [ Initialize Project ]
              <ArrowUpRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
